/**
 * SupplierAuditFindings — /suppliers/audit-findings (Supplier S4).
 *
 * The audit-findings library: every NCR + observation raised across supplier
 * audits, in one filterable register. Severity / status / type chip filters,
 * overdue flagging, and CAPA back-links. Answers "what's open against our
 * suppliers, and what's overdue?".
 */
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PageHero, StatCard, Stagger, Skeleton, EmptyState, Button } from '@edms/design-system';
import { useAuditFindingsQuery, type AuditFinding } from './useAuditFindingsQuery';
import styles from './SupplierAuditFindings.module.css';

type StatusFilter = 'ALL' | 'OPEN' | 'CLOSED';
type SevFilter = 'ALL' | 'CRITICAL' | 'MAJOR' | 'MINOR';
type TypeFilter = 'ALL' | 'NCR' | 'OBSERVATION';

const SEV_CLASS: Record<string, string> = { CRITICAL: 'sevCritical', MAJOR: 'sevMajor', MINOR: 'sevMinor' };
const STATUS_CLASS: Record<string, string> = { OPEN: 'stOpen', IN_PROGRESS: 'stProgress', CLOSED: 'stClosed' };

export function SupplierAuditFindings() {
  const { t, i18n } = useTranslation();
  const q = useAuditFindingsQuery();
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [sev, setSev] = useState<SevFilter>('ALL');
  const [type, setType] = useState<TypeFilter>('ALL');

  const dateFmt = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }), [i18n.language]);
  const all = useMemo(() => q.data?.items ?? [], [q.data]);
  const filtered = useMemo(() => all.filter((f) =>
    (status === 'ALL' || (status === 'OPEN' ? f.status !== 'CLOSED' : f.status === 'CLOSED')) &&
    (sev === 'ALL' || f.severity === sev) &&
    (type === 'ALL' || f.finding_type === type)
  ), [all, status, sev, type]);

  const chip = (active: boolean, label: string, onClick: () => void) => (
    <button type="button" className={`${styles.chip} ${active ? styles.chipActive : ''}`} onClick={onClick} aria-pressed={active}>{label}</button>
  );

  return (
    <div className={styles.screen}>
      <PageHero
        eyebrow={t('saf.eyebrow', { defaultValue: 'Suppliers' })}
        title={t('saf.title', { defaultValue: 'Audit findings library' })}
        subtitle={t('saf.subtitle', { defaultValue: 'Every NCR and observation raised across supplier audits — one filterable register with overdue flagging and CAPA back-links.' })}
      />

      {q.isLoading && (
        <>
          <div className={styles.kpiStrip} aria-hidden="true">{[0,1,2,3].map((i)=><Skeleton key={i} height={104} radius={14} />)}</div>
          {[0,1,2,3].map((i)=><Skeleton key={i} height={64} radius={10} />)}
        </>
      )}
      {q.isError && (
        <div className={styles.errorBanner} role="alert">
          <span>{t('saf.error', { defaultValue: 'Could not load audit findings.' })}</span>
          <Button variant="secondary" size="sm" onClick={() => q.refetch()}>{t('common.retry', { defaultValue: 'Retry' })}</Button>
        </div>
      )}

      {q.data && (
        <>
          <Stagger className={styles.kpiStrip} step={50}>
            <StatCard label={t('saf.kpi.total', { defaultValue: 'Findings' })} value={q.data.summary.total} accent="var(--spectro-purple, #9333EA)" deltaCaption={t('saf.kpi.ncr', { defaultValue: '{{n}} NCRs', n: q.data.summary.ncr })} />
            <StatCard label={t('saf.kpi.open', { defaultValue: 'Open' })} value={q.data.summary.open} accent="var(--spectro-blue, #0065FF)" />
            <StatCard label={t('saf.kpi.overdue', { defaultValue: 'Overdue' })} value={q.data.summary.overdue} accent={q.data.summary.overdue > 0 ? 'var(--color-status-error, #DC2626)' : 'var(--color-status-success, #16A34A)'} />
            <StatCard label={t('saf.kpi.critical', { defaultValue: 'Open critical' })} value={q.data.summary.critical} accent={q.data.summary.critical > 0 ? 'var(--color-status-error, #DC2626)' : 'var(--color-status-success, #16A34A)'} />
          </Stagger>

          <div className={styles.filters} role="group" aria-label={t('saf.filtersAria', { defaultValue: 'Finding filters' })}>
            <span className={styles.filterLabel}>{t('saf.status', { defaultValue: 'Status' })}</span>
            {chip(status==='ALL', t('saf.all', { defaultValue: 'All' }), () => setStatus('ALL'))}
            {chip(status==='OPEN', t('saf.openF', { defaultValue: 'Open' }), () => setStatus('OPEN'))}
            {chip(status==='CLOSED', t('saf.closedF', { defaultValue: 'Closed' }), () => setStatus('CLOSED'))}
            <span className={styles.filterDivider} />
            <span className={styles.filterLabel}>{t('saf.severity', { defaultValue: 'Severity' })}</span>
            {chip(sev==='ALL', t('saf.all', { defaultValue: 'All' }), () => setSev('ALL'))}
            {chip(sev==='CRITICAL', 'Critical', () => setSev('CRITICAL'))}
            {chip(sev==='MAJOR', 'Major', () => setSev('MAJOR'))}
            {chip(sev==='MINOR', 'Minor', () => setSev('MINOR'))}
            <span className={styles.filterDivider} />
            <span className={styles.filterLabel}>{t('saf.type', { defaultValue: 'Type' })}</span>
            {chip(type==='ALL', t('saf.all', { defaultValue: 'All' }), () => setType('ALL'))}
            {chip(type==='NCR', 'NCR', () => setType('NCR'))}
            {chip(type==='OBSERVATION', 'Observation', () => setType('OBSERVATION'))}
          </div>

          {filtered.length === 0 ? (
            <EmptyState icon="✓" title={t('saf.emptyTitle', { defaultValue: 'No findings match' })} description={t('saf.empty', { defaultValue: 'No audit findings match the current filters.' })} />
          ) : (
            <ul className={styles.list}>
              {filtered.map((f: AuditFinding) => (
                <li key={f.finding_id} className={`${styles.row} ${f.overdue ? styles.rowOverdue : ''}`}>
                  <div className={styles.rowMain}>
                    <span className={styles.findingId}>{f.finding_id}</span>
                    <span className={`${styles.badge} ${styles[SEV_CLASS[f.severity] ?? '']}`}>{f.severity}</span>
                    <span className={styles.typeBadge}>{f.finding_type}</span>
                    <span className={styles.findingTitle}>{f.title}</span>
                  </div>
                  <div className={styles.rowMeta}>
                    <Link to="/suppliers/$id" params={{ id: f.supplier_id }} className={styles.supLink}>{f.supplier_name}</Link>
                    <span className={styles.metaItem}>{f.audit_ref}</span>
                    {f.capa_ref && <span className={styles.metaItem}>{f.capa_ref}</span>}
                    {f.due_at_utc && (
                      <span className={`${styles.metaItem} ${f.overdue ? styles.overdueText : ''}`}>
                        {f.overdue ? t('saf.overdueDue', { defaultValue: 'Overdue · was due {{d}}', d: dateFmt.format(new Date(f.due_at_utc)) }) : t('saf.due', { defaultValue: 'Due {{d}}', d: dateFmt.format(new Date(f.due_at_utc)) })}
                      </span>
                    )}
                    <span className={`${styles.statusPill} ${styles[STATUS_CLASS[f.status] ?? '']}`}>{f.status.replace(/_/g, ' ')}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
